import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { switchProp } from 'styled-tools';
import router from 'next/router';
import { Position } from 'geojson';
import { IFindStorePage, IStoreModelIStoreInformation } from '$models';
import { useSWR, useTranslation } from '$hooks';
import { toStringParams } from '$lib/helpers/to-string-params.helper';
import { API_URL, imageUrl, mq, sortStoresWithZip } from '$lib/helpers';
import { supports } from '$lib/helpers/is-supported';
import { Flex } from '$components/layouts';
import { Loading } from '$components/elements/loading/loading';
import { StoreMapForm } from '$templates/find-store-page/store-map-form';
import { StoreList } from '$components/elements/store';
import { RouteKeys, useCMSRoute } from '~/store';
import { Text } from '$components/elements/text';
import { Map, useGEOJson } from '$components/elements/map';
import { Headline } from '$components/elements/headline';

type Props = IFindStorePage;

const defaultAmount = 4;
const defaultSearchParams = { lat: 0, lng: 0, limit: 500 };

export const StoreMap: FC<Props> = memo(({ defaultStores = [], ...restProps }) => {
    const [search, setSearch] = useState<string | null>(null);
    const [positionAllowed, setPositionAllowed] = useState<boolean>(false);
    const [amount, setAmount] = useState<number>(defaultAmount);
    const [searchParams, setSearchParams] = useState<{ lat: number; lng: number; limit: number } | null>(null);

    const { data, isLoading } = useSWR<IStoreModelIStoreInformation[]>(
        searchParams || search
            ? `${API_URL}/scom/api/stores/get${search ? '/' + search : '?' + toStringParams(searchParams)}`
            : ''
    );

    const stores = useMemo(
        () => (search ? sortStoresWithZip(data, search) : sortDefaultStores(data, defaultStores, positionAllowed)),
        [data, search, defaultStores, positionAllowed]
    );
    const position: Position | undefined = useMemo(
        () => (searchParams?.lat && searchParams?.lng ? [searchParams.lng, searchParams.lat] : undefined),
        [searchParams]
    );
    const { featureCollection } = useGEOJson(stores);
    const { routes } = useCMSRoute();

    const onSubmit = (term: string) => {
        setSearch(term);
        setAmount(defaultAmount);
    };

    const askForPosition = useCallback(() => {
        if (!supports.geolocation()) {
            setSearchParams(defaultSearchParams);
            setAmount(500);
            return;
        }

        // Set a timer in case the browser/user doesn't respond regarding geolocation
        const timeOut = setTimeout(() => {
            setSearchParams(defaultSearchParams);
            setAmount(500);
        }, 1000);

        navigator.geolocation.getCurrentPosition(
            ({ coords: { latitude, longitude } }) => {
                setSearchParams({ ...defaultSearchParams, lat: latitude, lng: longitude });
                setAmount(defaultAmount);
                setPositionAllowed(true);
                clearTimeout(timeOut);
            },
            () => {
                setSearchParams(defaultSearchParams);
                setAmount(500);
                clearTimeout(timeOut);
            }
        );
    }, [searchParams]);

    const resetZip = useCallback(() => {
        setSearch(null);
    }, []);

    useEffect(() => {
        if (!search) {
            askForPosition();
        }

        return () => {
            setSearchParams(null);
        };
    }, []);

    const { translate } = useTranslation();

    return (
        <>
            <StoreMapForm onSubmit={onSubmit} onReset={resetZip} positionAllowed={positionAllowed} {...restProps} />
            <Flex alignItems="center" justifyContent="center" column>
                <StyledHeadline size={3}>
                    {positionAllowed && !search
                        ? translate('findStore.searchResultHeading')
                        : translate('findStore.searchDefaultHeading')}
                </StyledHeadline>
                <Text variant="body" bottomMargin="8" dimmed>
                    {translate('findStore.searchResultText')}
                </Text>
            </Flex>

            <Container state={isLoading ? 'loading' : ''}>
                {searchParams || search ? (
                    <>
                        <StoreList
                            storeCollection={(stores || [])?.slice(0, amount)}
                            onFetchMore={() => setAmount(amount + defaultAmount)}
                            displayFetchMore={(data || []).length > amount}
                            isLoading={isLoading}
                            userPosition={position}
                        />

                        <Map
                            featureCollection={featureCollection}
                            markerPaths={imageUrl(restProps.markerStandard?.url ?? '')}
                            onMarkerClick={(store) =>
                                store && router.push(`${routes[RouteKeys.StoreDetail]}/${store.customerWebName}`)
                            }
                        >
                            <MapContainer id="map" />
                        </Map>
                    </>
                ) : (
                    <Loading />
                )}
            </Container>
        </>
    );
});

const sortDefaultStores = (
    stores?: IStoreModelIStoreInformation[],
    defaultStores?: string[],
    isGeolocationUsed?: boolean
) => {
    if (!stores || !defaultStores?.length || isGeolocationUsed) {
        return stores;
    }
    const storesToSort = [...stores];
    const mappedDefaultStores = defaultStores
        .map((storeId) => {
            const foundIndex = storesToSort.findIndex((sortStore) => sortStore.customerId === storeId);
            if (foundIndex > -1) {
                const store = storesToSort.splice(foundIndex, 1);
                return store[0];
            }
            return null;
        })
        .filter((store) => !!store) as IStoreModelIStoreInformation[];

    return mappedDefaultStores.concat(storesToSort);
};

const Container = styled(Flex)<{ state: 'loading' | 'error' | '' }>(
    () => ({
        transition: 'opacity 200ms ease',

        display: 'flex',
        flexDirection: 'column',
        [mq('lg')]: {
            flexDirection: 'row',
            height: '600px',
        },
    }),
    switchProp('state', {
        loading: { opacity: '0.5' },
    })
);

const StyledHeadline = styled(Headline)({
    marginBottom: 0,
});

const MapContainer = styled.div({
    height: '400px',
    [mq('lg')]: {
        flex: 1,
        height: 'auto',
    },
});
